import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Stars } from './shared';

function riskColor(rating) {
  if (!rating) return '#6b8dad';
  if (rating.includes('🟢') || /low/i.test(rating))  return '#059669';
  if (rating.includes('🔴') || /high/i.test(rating)) return '#dc2626';
  return '#d97706';
}

function RiskRow({ label, rating, reason, t }) {
  const color = riskColor(rating);
  return (
    <div style={s.riskRow}>
      <div style={s.riskLeft}>
        <span style={{ ...s.riskDot, background: color }} />
        <span style={s.riskLabel}>{label}</span>
      </div>
      <div style={s.riskBar}>
        <div style={{ ...s.riskFill, background: color, width: color === '#059669' ? '25%' : color === '#dc2626' ? '85%' : '55%' }} />
      </div>
      <span style={{ ...s.riskRating, color }}>
        {color === '#059669' ? t('potentialCard.riskLow') : color === '#dc2626' ? t('potentialCard.riskHigh') : t('potentialCard.riskMedium')}
      </span>
      {reason && <span style={s.riskReason}>{reason}</span>}
    </div>
  );
}

export default function PotentialCard({ data }) {
  const { t } = useTranslation();
  return (
    <Card title={t('potentialCard.title')} icon="🎯" accent="#d97706">
      {data.verdictStars > 0 && (
        <div style={s.verdictHero}>
          <div style={s.verdictLeft}>
            <Stars n={data.verdictStars} size={24} />
            <span style={s.verdictNum}>{data.verdictStars}/5</span>
          </div>
          {data.verdictText && <p style={s.verdictText}>{data.verdictText}</p>}
        </div>
      )}

      <div style={s.casesGrid}>
        <div style={s.bull}>
          <div style={s.caseHeader}>
            <span style={{ fontSize: 16 }}>🐂</span>
            <span style={s.caseTitle}>Bull Case</span>
          </div>
          <p style={s.caseText}>{data.bull || '—'}</p>
        </div>
        <div style={s.bear}>
          <div style={s.caseHeader}>
            <span style={{ fontSize: 16 }}>🐻</span>
            <span style={s.caseTitle}>Bear Case</span>
          </div>
          <p style={s.caseText}>{data.bear || '—'}</p>
        </div>
      </div>

      {data.risks.length > 0 && (
        <div style={s.riskMatrix}>
          <div style={s.matrixLabel}>{t('potentialCard.riskMatrix')}</div>
          {data.risks.map((r, i) => <RiskRow key={i} label={r.label} rating={r.rating} reason={r.reason} t={t} />)}
        </div>
      )}
    </Card>
  );
}

const s = {
  verdictHero: {
    background: 'linear-gradient(135deg, rgba(217,119,6,0.1), rgba(234,88,12,0.07))',
    border: '1px solid rgba(217,119,6,0.25)',
    borderRadius: 12, padding: '14px 18px', marginBottom: 16,
    display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap',
  },
  verdictLeft: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  verdictNum: { fontSize: 28, fontWeight: 800, color: '#d97706' },
  verdictText: { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0, flex: 1 },
  casesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  bull: { background: 'rgba(5,150,105,0.07)', border: '1px solid rgba(5,150,105,0.2)', borderRadius: 10, padding: '12px 14px' },
  bear: { background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, padding: '12px 14px' },
  caseHeader: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 },
  caseTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text)' },
  caseText: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 },
  riskMatrix: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  matrixLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 },
  riskRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  riskLeft: { display: 'flex', alignItems: 'center', gap: 7, minWidth: 140 },
  riskDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  riskLabel: { fontSize: 12, color: 'var(--text-muted)' },
  riskBar: { flex: 1, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', minWidth: 60 },
  riskFill: { height: '100%', borderRadius: 2, transition: 'width 0.5s ease' },
  riskRating: { fontSize: 11, fontWeight: 700, minWidth: 28 },
  riskReason: { fontSize: 11, color: 'var(--text-dim)', flex: 2 },
};
